/**
 * Google Consent Mode v2 - Kullanıcı rızası yönetimi
 * AEA, UK ve İsviçre uyumu için
 */

const CONSENT_STORAGE_KEY = 'tservis_consent_v1';

export type ConsentState = 'granted' | 'denied' | 'pending';

export interface ConsentPreferences {
  ad_storage: ConsentState;
  ad_user_data: ConsentState;
  ad_personalization: ConsentState;
  analytics_storage: ConsentState;
  timestamp: number;
}

const defaultDenied: ConsentPreferences = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  timestamp: 0
};

export function getStoredConsent(): ConsentPreferences | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentPreferences;
    return parsed && typeof parsed.ad_storage === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

export function setStoredConsent(prefs: ConsentPreferences): void {
  try {
    prefs.timestamp = Date.now();
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

export function hasConsentChoice(): boolean {
  return getStoredConsent() !== null;
}

/** Tüm reklam/analytics izinlerini ver */
export function consentGrantAll(): ConsentPreferences {
  const prefs: ConsentPreferences = {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
    timestamp: Date.now()
  };
  setStoredConsent(prefs);
  updateGtagConsent(prefs);
  return prefs;
}

/** Tüm reklam/analytics izinlerini reddet */
export function consentDenyAll(): ConsentPreferences {
  const prefs: ConsentPreferences = {
    ...defaultDenied,
    timestamp: Date.now()
  };
  setStoredConsent(prefs);
  updateGtagConsent(prefs);
  return prefs;
}

/** Özel tercihleri kaydet ve gtag güncelle */
export function consentUpdate(prefs: Partial<ConsentPreferences>): ConsentPreferences {
  const current = getStoredConsent() || { ...defaultDenied, timestamp: 0 };
  const next: ConsentPreferences = {
    ad_storage: prefs.ad_storage ?? current.ad_storage,
    ad_user_data: prefs.ad_user_data ?? current.ad_user_data,
    ad_personalization: prefs.ad_personalization ?? current.ad_personalization,
    analytics_storage: prefs.analytics_storage ?? current.analytics_storage,
    timestamp: Date.now()
  };
  setStoredConsent(next);
  updateGtagConsent(next);
  return next;
}

/** Google gtag consent güncelle */
function updateGtagConsent(prefs: ConsentPreferences): void {
  try {
    if (typeof window === 'undefined' || !(window as any).gtag) return;
    (window as any).gtag('consent', 'update', {
      ad_storage: prefs.ad_storage,
      ad_user_data: prefs.ad_user_data,
      ad_personalization: prefs.ad_personalization,
      analytics_storage: prefs.analytics_storage
    });
  } catch {
    // gtag yoksa sessizce geç
  }
}

/** Sayfa yüklendiğinde kayıtlı rıza varsa gtag'i güncelle */
export function applyStoredConsent(): void {
  const stored = getStoredConsent();
  if (stored) {
    updateGtagConsent(stored);
  }
}
